import { useEffect } from 'react';
import { onlineManager } from '@tanstack/react-query';

import { SignalStreamError, readSignalStream } from '../../api/stream.api';
import type { NotificationSignal } from '../../types/domain.types';
import { useLatest } from '../logic/useLatest';
import type { Latest } from '../logic/useLatest';

export interface SignalStreamOptions {
  readonly onReady: () => void;
  readonly onSignal: (signal: NotificationSignal) => void;
  readonly renew?: () => Promise<unknown>;
  readonly onGone?: () => void;
}

const FIRST_RETRY_MS = 1_000;
const LONGEST_RETRY_MS = 30_000;
const SILENCE_LIMIT_MS = 60_000;

const REFUSED: ReadonlySet<number> = new Set([401, 403]);
const GONE: ReadonlySet<number> = new Set([404, 410]);

type Waker = (wake: () => void) => () => void;

const afterMs =
  (ms: number): Waker =>
  (wake) => {
    const timer = setTimeout(wake, ms);
    return () => clearTimeout(timer);
  };

const whenAborted =
  (signal: AbortSignal): Waker =>
  (wake) => {
    signal.addEventListener('abort', wake);
    return () => signal.removeEventListener('abort', wake);
  };

const whenOnlineIs =
  (online: boolean): Waker =>
  (wake) =>
    onlineManager.subscribe((now) => {
      if (now === online) wake();
    });

const armAll = (wakers: readonly Waker[], wake: () => void): (() => void) => {
  const disarms = wakers.map((arm) => arm(wake));
  return () => disarms.forEach((disarm) => disarm());
};

const firstOf = (wakers: readonly Waker[]): Promise<void> =>
  new Promise((resolve) => {
    const disarms: Array<() => void> = [];
    const wake = (): void => {
      disarms.forEach((disarm) => disarm());
      resolve();
    };
    wakers.forEach((arm) => disarms.push(arm(wake)));
  });

const untilOnline = async (signal: AbortSignal): Promise<void> => {
  if (signal.aborted || onlineManager.isOnline()) return;
  await firstOf([whenOnlineIs(true), whenAborted(signal)]);
};

const pause = async (ms: number, signal: AbortSignal): Promise<void> => {
  if (signal.aborted) return;
  await firstOf([afterMs(ms), whenOnlineIs(true), whenAborted(signal)]);
};

const watchdog = (ms: number, onSilence: () => void): { readonly heard: () => void; readonly stop: () => void } => {
  let timer = setTimeout(onSilence, ms);
  return {
    heard: () => {
      clearTimeout(timer);
      timer = setTimeout(onSilence, ms);
    },
    stop: () => clearTimeout(timer),
  };
};

const refusalOf = (error: unknown): SignalStreamError | null => (error instanceof SignalStreamError ? error : null);

const askedWaitMs = (refusal: SignalStreamError | null): number => Math.min(refusal?.retryAfterMs ?? 0, LONGEST_RETRY_MS);

const renewQuietly = (renew: (() => Promise<unknown>) | undefined): Promise<unknown> =>
  renew === undefined ? Promise.resolve() : Promise.resolve().then(renew).catch(() => undefined);

const listenOnce = async (
  url: string,
  signal: AbortSignal,
  latest: Latest<SignalStreamOptions>,
  onReady: () => void,
): Promise<SignalStreamError | null> => {
  const connection = new AbortController();
  const drop = (): void => connection.abort();
  const silence = watchdog(SILENCE_LIMIT_MS, drop);
  const disarm = armAll([whenAborted(signal), whenOnlineIs(false)], drop);
  try {
    await readSignalStream(url, {
      signal: connection.signal,
      onActivity: silence.heard,
      onReady,
      onSignal: (notification) => latest.current.onSignal(notification),
    });
    return null;
  } catch (error) {
    return refusalOf(error);
  } finally {
    silence.stop();
    disarm();
  }
};

const keepListening = async (url: string, signal: AbortSignal, latest: Latest<SignalStreamOptions>): Promise<void> => {
  let retryMs = FIRST_RETRY_MS;
  const onReady = (): void => {
    retryMs = FIRST_RETRY_MS;
    latest.current.onReady();
  };
  while (!signal.aborted) {
    await untilOnline(signal);
    if (signal.aborted) return;
    const refusal = await listenOnce(url, signal, latest, onReady);
    if (signal.aborted) return;
    if (refusal !== null && GONE.has(refusal.status)) {
      latest.current.onGone?.();
      return;
    }
    if (refusal !== null && REFUSED.has(refusal.status)) await renewQuietly(latest.current.renew);
    if (onlineManager.isOnline()) {
      await pause(Math.max(retryMs, askedWaitMs(refusal)), signal);
      retryMs = Math.min(retryMs * 2, LONGEST_RETRY_MS);
    }
  }
};

export const useSignalStream = (url: string | null, options: SignalStreamOptions): void => {
  const latest = useLatest(options);
  useEffect(() => {
    if (url === null) return undefined;
    const listening = new AbortController();
    void keepListening(url, listening.signal, latest);
    return () => listening.abort();
  }, [url, latest]);
};
